import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import Uppy from "@uppy/core";
import Papa, { ParseResult } from "papaparse";
import "@uppy/core/dist/style.css";
import bulkImportProspects from "api/canprospect/bulkImportProspects";
import { ProspectInterface } from "api/canprospect/prospect";

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({ open, onClose }) => {
  const [uppyInstance, setUppyInstance] = useState<Uppy | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const dropAreaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: [".csv"],
      },
      autoProceed: true,
    });

    uppy.on("file-added", (file) => {
      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const csvContent = e.target?.result;

        if (typeof csvContent === "string") {
          setIsProcessing(true);

          Papa.parse(csvContent, {
            header: true,
            complete: async (result: ParseResult<Record<string, string>>) => {
              if (result.data.length > 0) {
                const formattedData: ProspectInterface[] = result.data.map((row) => ({
                  CompanyName: row.CompanyName || null,
                  FirstName: row.FirstName || null,
                  LastName: row.LastName || null,
                  Phone: row.Phone || null,
                  Email: row.Email || null,
                  Address: row.Address || null,
                  City: row.City || null,
                  Province: row.Province || null,
                  PostalCode: row.PostalCode || null,
                  TitleDescription: row.TitleDescription || null,
                  SicCodeDescription: row.SicCodeDescription || null,
                  ConsultantId: row.ConsultantId ? parseInt(row.ConsultantId, 10) : null,
                  ProspectStatusId: row.ProspectStatusId ? parseInt(row.ProspectStatusId, 10) : null,
                  ProspectStatus: row.ProspectStatus || null,
                  Website: row.Website || null,
                  ProspectComment: row.ProspectComment || null,
                }));

                try {
                  await bulkImportProspects(formattedData);
                  setToastOpen(true);
                  onClose();
                } catch (error) {
                  console.error("Error during bulk import:", error);
                  alert("An error occurred during the bulk import. Please try again.");
                } finally {
                  setIsProcessing(false);
                }
              } else {
                alert("The uploaded CSV file is empty or invalid.");
                setIsProcessing(false);
              }
            },
            error: () => {
              alert("An error occurred while parsing the CSV file. Please try again.");
              setIsProcessing(false);
            },
          });
        }
      };

      if (file.data) {
        reader.readAsText(file.data as Blob);
      }
    });

    setUppyInstance(uppy);

    return () => {
      uppy.close();
    };
  }, [onClose]);

  useEffect(() => {
    const dropArea = dropAreaRef.current;

    if (!dropArea) return;

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      dropArea.style.backgroundColor = "#f1f1f1";
    };

    const handleDragLeave = () => {
      if (dropArea) {
        dropArea.style.backgroundColor = "#f9f9f9";
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      if (dropArea) {
        dropArea.style.backgroundColor = "#f9f9f9";
      }

      const file = event.dataTransfer?.files[0];
      if (file && uppyInstance) {
        uppyInstance.addFile({
          name: file.name,
          type: file.type,
          data: file,
        });
      }
    };

    dropArea.addEventListener("dragover", handleDragOver);
    dropArea.addEventListener("dragleave", handleDragLeave);
    dropArea.addEventListener("drop", handleDrop);

    return () => {
      dropArea.removeEventListener("dragover", handleDragOver);
      dropArea.removeEventListener("dragleave", handleDragLeave);
      dropArea.removeEventListener("drop", handleDrop);
    };
  }, [uppyInstance]);

  const handleBrowseClick = () => {
    if (uppyInstance) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".csv";
      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement)?.files?.[0];
        if (file) {
          uppyInstance.addFile({
            name: file.name,
            type: file.type,
            data: file,
          });
        }
      };
      input.click();
    }
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Bulk Import Prospects
          </Typography>
        </DialogTitle>
        <DialogContent>
          {isProcessing ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="200px"
            >
              <CircularProgress color="primary" />
              <Typography variant="body1" color="textSecondary" mt={2}>
                Processing...
              </Typography>
            </Box>
          ) : (
            <Box
              ref={dropAreaRef}
              sx={{
                width: "100%",
                height: "200px",
                border: "2px dashed #cccccc",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f1f1f1",
                },
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Drag and drop a file here or browse files.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBrowseClick}
                sx={{ mt: 2 }}
              >
                Browse
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="text" color="secondary" disabled={isProcessing}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleToastClose} severity="success" sx={{ width: "100%" }}>
          Bulk import completed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkImportModal;
